import { useCallback, useMemo } from 'react';
import { useMemoWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/hooks/index.tsx';
import { useEnabledHiddenFields as useHiddenFieldsByUim } from '@atlassian/ui-modifications-core/controllers';
import type { ContainersByType } from '@atlassian/jira-issue-layout-common-constants/src/index.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	useRouterStoreActions,
	createRouterSelector,
	type RouterState,
} from '@atlassian/react-resource-router';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import {
	useIsFullPageIssueView,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { USER_LAYOUT_PREFERENCE } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import {
	useProjectKey as useProjectKeyNew,
	useProjectType as useProjectTypeNew,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	SOFTWARE_PROJECT,
	CORE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { Tabs, defaultActiveTab } from '@atlassian/jira-issue-view-common-constants/src/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import isMobileUserAgent from '@atlassian/jira-common-is-mobile-user-agent/src/index.tsx';
import {
	useIssueLayoutActionsStore,
	useIssueLayoutGlanceStore,
	useIssueLayoutContextPanelStore,
	useIssueLayoutContainersStore,
} from './context.tsx';
import type { Glance, ContextPanelItem, ActiveTabSetter, UseActiveTabReturn } from './types.tsx';

import { filterOutItemsWithProvidedIds } from './utils.tsx';

export const useIssueLayoutContainers = (issueKey: IssueKey) => {
	const [containers] = useIssueLayoutContainersStore(issueKey);
	return [containers] as const;
};

export const useLayoutContainerByType = (issueKey: IssueKey, type: string) => {
	const [containers] = useIssueLayoutContainersStore(issueKey);
	const container = containers.find((innerContainer) => innerContainer.containerType === type);
	// Forge app with UI Modifications module can hide selected fields using
	// UI modifications Field API. We are removing them here from the layout fields
	const fieldsHiddenByUim = useHiddenFieldsByUim();

	return useMemoWithCondition(
		() => [filterOutItemsWithProvidedIds(container?.items.nodes ?? [], fieldsHiddenByUim)] as const,
		[container?.items.nodes, fieldsHiddenByUim],
		() => fg('jfp_magma_issue-stability-phase1'),
	);
};

export const useLayoutContainerByTypeMemoised = (issueKey: IssueKey, type: string) => {
	const [containers] = useIssueLayoutContainersStore(issueKey);
	const container = containers.find((innerContainer) => innerContainer.containerType === type);
	const fieldsHiddenByUim = useHiddenFieldsByUim();
	const filteredItems = useMemo(() => {
		return filterOutItemsWithProvidedIds(container?.items.nodes ?? [], fieldsHiddenByUim);
	}, [container, fieldsHiddenByUim]);
	return [filteredItems] as const;
};

export const useIssueLayoutGlance = (issueKey: IssueKey) => {
	const [glance] = useIssueLayoutGlanceStore(issueKey);

	return [glance] as const;
};

export const useIssueLayoutContextPanel = (issueKey: IssueKey) => {
	const [contextPanel] = useIssueLayoutContextPanelStore(issueKey);

	return [contextPanel] as const;
};

const useUserPreferredWorkItemLayoutwithFFOn = () => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKeyNew(issueKey);
	const projectType = useProjectTypeNew(projectKey);
	const isFullIssueView = useIsFullPageIssueView();
	const [userPreferredLayout] = useUserPreferencesValue(USER_LAYOUT_PREFERENCE);
	const isMobile = isMobileUserAgent();
	const locale = useLocale();

	try {
		// if neither tab layout nor issue view layout experiment is enabled return defaults
		if (
			!fg('issue_view_layout_experiment_feature_gate') &&
			!fg('issue_view_tab_layout_feature_gate')
		) {
			if (fg('thor_layouts_splunk_logs_enable')) {
				log.safeErrorWithoutCustomerData(
					'issue-view-layout.services.main',
					`useUserPreferredWorkItemLayout: Kill switch is off with flag values: ${JSON.stringify({
						isMobile,
						isFullIssueView,
						projectType,
						isIssueKeyNull: issueKey === null,
						isProjectKeyNull: projectKey === null,
						isProjectTypeNull: projectType === null,
						locale,
						userPreferredLayout,
						isWideLayoutEnabled: expVal(
							'issue_view_layout_experiment',
							'isWideLayoutEnabled',
							false,
						),
						isDiscussionLayoutEnabled: expVal(
							'issue_view_layout_experiment',
							'isDiscussionLayoutEnabled',
							false,
						),
					})}`,
				);
			}
			return {
				isInDiscussionLayoutExperiment: false,
				isInWideLayoutExperiment: false,
				isInLayoutExperiment: false,
				isActivityInVerticalSidePanel: false,
				userPreferredLayout: null,
			};
		}
		const isSoftwareOrBusiness = projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT;

		const shouldBeInLayoutExperiment = !isMobile && isSoftwareOrBusiness && isFullIssueView;
		const shouldBeInLayoutExperimentAndTabLayoutEnabled =
			fg('issue_view_tab_layout_feature_gate') && shouldBeInLayoutExperiment;

		if (!shouldBeInLayoutExperiment) {
			return {
				isInDiscussionLayoutExperiment: false,
				isInWideLayoutExperiment: false,
				isInLayoutExperiment: false,
				isActivityInVerticalSidePanel: false,
				userPreferredLayout: null,
			};
		}

		const isInTabLayoutExperiment =
			shouldBeInLayoutExperimentAndTabLayoutEnabled &&
			expVal('issue_view_tab_layout_experiment', 'isTabLayoutEnabled', false);

		const isInWideLayoutExperiment =
			shouldBeInLayoutExperiment &&
			expVal('issue_view_layout_experiment', 'isWideLayoutEnabled', false);

		const isInDiscussionLayoutExperiment =
			shouldBeInLayoutExperiment &&
			expVal('issue_view_layout_experiment', 'isDiscussionLayoutEnabled', false);

		const isInLayoutExperiment =
			isInWideLayoutExperiment || isInDiscussionLayoutExperiment || isInTabLayoutExperiment;

		const userLayoutType =
			isInLayoutExperiment && userPreferredLayout && typeof userPreferredLayout === 'string'
				? JSON.parse(userPreferredLayout)?.layoutType
				: null;

		if (
			userLayoutType === null &&
			(expVal('issue_view_layout_experiment', 'isDiscussionLayoutEnabled', false) ||
				expVal('issue_view_layout_experiment', 'isWideLayoutEnabled', false)) &&
			fg('thor_layouts_splunk_logs_enable')
		) {
			log.safeErrorWithoutCustomerData(
				'issue-view-layout.services.main',
				`useUserPreferredWorkItemLayout: User layout type parsing: ${JSON.stringify({
					userPreferredLayout,
					userLayoutType,
					isMobile,
					isIssueKeyNull: issueKey === null,
					isProjectKeyNull: projectKey === null,
					isProjectTypeNull: projectType === null,
					locale,
					isSoftwareOrBusiness,
					isFullIssueView,
					isWideLayoutEnabled: expVal('issue_view_layout_experiment', 'isWideLayoutEnabled', false),
					isDiscussionLayoutEnabled: expVal(
						'issue_view_layout_experiment',
						'isDiscussionLayoutEnabled',
						false,
					),
				})}`,
			);
		}
		let userSelectedLayout = userLayoutType;
		if (!fg('thor-make-layout-options-work-together')) {
			if (isInLayoutExperiment && isInDiscussionLayoutExperiment && userLayoutType === 'WIDE') {
				userSelectedLayout = 'STANDARD';
			}
			if (isInLayoutExperiment && isInWideLayoutExperiment && userLayoutType === 'DISCUSSION') {
				userSelectedLayout = 'STANDARD';
			}
		}

		if (
			userSelectedLayout === null &&
			(expVal('issue_view_layout_experiment', 'isDiscussionLayoutEnabled', false) ||
				expVal('issue_view_layout_experiment', 'isWideLayoutEnabled', false)) &&
			fg('thor_layouts_splunk_logs_enable')
		) {
			log.safeErrorWithoutCustomerData(
				'issue-view-layout.services.main',
				`useUserPreferredWorkItemLayout: User selected layout parsing: ${JSON.stringify({
					userSelectedLayout,
					isInLayoutExperiment,
					userPreferredLayout,
					isMobile,
					isIssueKeyNull: issueKey === null,
					isProjectKeyNull: projectKey === null,
					isProjectTypeNull: projectType === null,
					locale,
					isSoftwareOrBusiness,
					isFullIssueView,
					isWideLayoutEnabled: expVal('issue_view_layout_experiment', 'isWideLayoutEnabled', false),
					isDiscussionLayoutEnabled: expVal(
						'issue_view_layout_experiment',
						'isDiscussionLayoutEnabled',
						false,
					),
				})}`,
			);
		}

		// isActivityInVerticalSidePanel is not needed and can be derived from userSelectedLayout
		// but we keep it to avoid making too many changes in the codebase
		const isActivityInVerticalSidePanel =
			isInDiscussionLayoutExperiment && userSelectedLayout === 'DISCUSSION';

		return {
			isInDiscussionLayoutExperiment: Boolean(isInDiscussionLayoutExperiment),
			isInWideLayoutExperiment: Boolean(isInWideLayoutExperiment),
			isInLayoutExperiment: Boolean(isInLayoutExperiment),
			// isActivityInVerticalSidePanel is not needed and can be derived from userSelectedLayout
			// but we keep it to avoid making too many changes in the codebase
			isActivityInVerticalSidePanel: Boolean(isActivityInVerticalSidePanel),
			userPreferredLayout: userSelectedLayout,
		};
	} catch (error) {
		log.safeErrorWithoutCustomerData(
			'issue-view-layout.services.main',
			`Error in useUserPreferredWorkItemLayout : ${JSON.stringify({
				error,
				isMobile,
				isFullIssueView,
				isIssueKeyNull: issueKey === null,
				isProjectKeyNull: projectKey === null,
				isProjectTypeNull: projectType === null,
				userPreferredLayout,
				locale,
				isInWideLayoutExperiment: expVal(
					'issue_view_layout_experiment',
					'isWideLayoutEnabled',
					false,
				),
				isInDiscussionLayoutExperiment: expVal(
					'issue_view_layout_experiment',
					'isDiscussionLayoutEnabled',
					false,
				),
			})}`,
		);
		return {
			isInDiscussionLayoutExperiment: false,
			isInWideLayoutExperiment: false,
			isInLayoutExperiment: false,
			isActivityInVerticalSidePanel: false,
			userPreferredLayout: null,
		};
	}
};

const useUserPreferredWorkItemLayoutwithFFOff = () => {
	try {
		// if neither tab layout nor issue view layout experiment is enabled return defaults
		if (
			!fg('issue_view_layout_experiment_feature_gate') &&
			!fg('issue_view_tab_layout_feature_gate')
		) {
			return {
				isInDiscussionLayoutExperiment: false,
				isInWideLayoutExperiment: false,
				isInLayoutExperiment: false,
				isActivityInVerticalSidePanel: false,
				userPreferredLayout: null,
			};
		}

		// eslint-disable-next-line react-hooks/react-compiler
		/* eslint-disable react-hooks/rules-of-hooks */
		const issueKey = useIssueKey();
		const projectKey = useProjectKeyNew(issueKey);
		const projectType = useProjectTypeNew(projectKey);
		const isFullIssueView = useIsFullPageIssueView();
		const [userPreferredLayout] = useUserPreferencesValue(USER_LAYOUT_PREFERENCE);
		const isMobile = isMobileUserAgent();
		const isSoftwareOrBusiness = projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT;
		const shouldBeInLayoutExperiment = !isMobile && isSoftwareOrBusiness && isFullIssueView;
		const shouldBeInLayoutExperimentAndTabLayoutEnabled =
			fg('issue_view_tab_layout_feature_gate') && shouldBeInLayoutExperiment;

		if (!shouldBeInLayoutExperiment) {
			return {
				isInDiscussionLayoutExperiment: false,
				isInWideLayoutExperiment: false,
				isInLayoutExperiment: false,
				isActivityInVerticalSidePanel: false,
				userPreferredLayout: null,
			};
		}

		const isInTabLayoutExperiment =
			shouldBeInLayoutExperimentAndTabLayoutEnabled &&
			expVal('issue_view_tab_layout_experiment', 'isTabLayoutEnabled', false);

		const isInWideLayoutExperiment =
			shouldBeInLayoutExperiment &&
			expVal('issue_view_layout_experiment', 'isWideLayoutEnabled', false);

		const isInDiscussionLayoutExperiment =
			shouldBeInLayoutExperiment &&
			expVal('issue_view_layout_experiment', 'isDiscussionLayoutEnabled', false);

		const isInLayoutExperiment =
			isInWideLayoutExperiment || isInDiscussionLayoutExperiment || isInTabLayoutExperiment;

		const userLayoutType =
			isInLayoutExperiment && userPreferredLayout && typeof userPreferredLayout === 'string'
				? JSON.parse(userPreferredLayout)?.layoutType
				: null;
		/* eslint-enable react-hooks/rules-of-hooks */

		let userSelectedLayout = userLayoutType;
		if (!fg('thor-make-layout-options-work-together')) {
			if (isInLayoutExperiment && isInDiscussionLayoutExperiment && userLayoutType === 'WIDE') {
				userSelectedLayout = 'STANDARD';
			}
			if (isInLayoutExperiment && isInWideLayoutExperiment && userLayoutType === 'DISCUSSION') {
				userSelectedLayout = 'STANDARD';
			}
		}

		// isActivityInVerticalSidePanel is not needed and can be derived from userSelectedLayout
		// but we keep it to avoid making too many changes in the codebase
		const isActivityInVerticalSidePanel =
			isInDiscussionLayoutExperiment && userSelectedLayout === 'DISCUSSION';

		return {
			isInDiscussionLayoutExperiment: Boolean(isInDiscussionLayoutExperiment),
			isInWideLayoutExperiment: Boolean(isInWideLayoutExperiment),
			isInLayoutExperiment: Boolean(isInLayoutExperiment),
			// isActivityInVerticalSidePanel is not needed and can be derived from userSelectedLayout
			// but we keep it to avoid making too many changes in the codebase
			isActivityInVerticalSidePanel: Boolean(isActivityInVerticalSidePanel),
			userPreferredLayout: userSelectedLayout,
		};
	} catch (error) {
		log.safeErrorWithoutCustomerData(
			'issue-view-layout.services.main',
			`Error in useUserPreferredWorkItemLayout : ${error}`,
		);
		return {
			isInDiscussionLayoutExperiment: false,
			isInWideLayoutExperiment: false,
			isInLayoutExperiment: false,
			isActivityInVerticalSidePanel: false,
			userPreferredLayout: null,
		};
	}
};

export const useUserPreferredWorkItemLayout = functionWithCondition(
	() => fg('issue_layout_rule_of_hook_fix'),
	useUserPreferredWorkItemLayoutwithFFOn,
	useUserPreferredWorkItemLayoutwithFFOff,
);

export const useIssueLayoutActions = () => {
	const [, actions] = useIssueLayoutActionsStore();

	const setIssueViewContainersLayout = useCallback(
		(issueKey: IssueKey, containersByType: ContainersByType) => {
			actions.setIssueViewContainersLayout(issueKey, containersByType);
		},
		[actions],
	);

	const setIssueViewLayoutGlance = useCallback(
		(issueKey: IssueKey, glance?: Glance) => {
			actions.setIssueViewLayoutGlance(issueKey, glance);
		},
		[actions],
	);

	const setIssueViewLayoutContextPanel = useCallback(
		(issueKey: IssueKey, newContextPanel?: ContextPanelItem) => {
			actions.setIssueViewLayoutContextPanel(issueKey, newContextPanel);
		},
		[actions],
	);

	return [
		null,
		{
			setIssueViewContainersLayout,
			setIssueViewLayoutGlance,
			setIssueViewLayoutContextPanel,
		},
	] as const;
};

/**
 * useRouteToGetQueryParams hook
 * This hook retrieves the current query parameters from the router state.
 * It is used to access the active tab and other query parameters in the issue view layout.
 */
const useRouteToGetQueryParams = createRouterSelector((state: RouterState) => {
	return state.query;
});

/**
 * useActiveTab hook
 * This hook provides functions to set the active tab in the issue view layout.
 * It uses the router store to update the query parameters for the active tab.
 */

export const useActiveTab: UseActiveTabReturn = () => {
	const { activeTab = defaultActiveTab, ...otherQueryParams } = useRouteToGetQueryParams();
	const { updateQueryParam } = useRouterStoreActions();

	const setActiveTab: ActiveTabSetter = useCallback(
		(tab) => {
			if (activeTab !== tab) {
				updateQueryParam({ ...otherQueryParams, activeTab: tab }, 'replace');
			}
		},
		[activeTab, otherQueryParams, updateQueryParam],
	);

	return useMemo(
		() => ({
			setDescriptionAsActive: () => setActiveTab(Tabs.Description),
			setWorkItemsAsActive: () => setActiveTab(Tabs.WorkItems),
			setIntegrationsAsActive: () => setActiveTab(Tabs.Integrations),
			setResourcesAsActive: () => setActiveTab(Tabs.Resources),
			currentActiveTab: activeTab,
		}),
		[activeTab, setActiveTab],
	);
};
